import api from "./api";
import type { PanelPermission, AccessMember } from "./access";

// Employees who hold a role share the same shape as access members.
export type RoleMember = AccessMember;

export interface Role {
  id: string;
  name: string;
  role_key: string;
  rank: number;
  default_access_id: string | null;
  is_system: boolean;
  is_active?: boolean;
  branchID?: string | null;
  assigned_count?: number;
}

export const listRoles = async (): Promise<Role[]> => {
  const res = await api.get("/roles");
  const roles: Role[] = Array.isArray(res.data) ? res.data : [];
  // Defensive de-dup by role_key: if the backend ever has duplicate role rows
  // (e.g. a multi-worker seeding race created several "intern" rows), only show
  // each role once so the dropdown doesn't repeat it.
  const seen = new Set<string>();
  return roles.filter((r) => {
    const key = (r.role_key || r.name || r.id || "").trim().toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const createRole = async (body: {
  name: string;
  rank?: number;
  permissions: Record<string, PanelPermission>;
}): Promise<Role> => {
  const res = await api.post("/roles", body);
  return res.data;
};

export const updateRole = async (
  id: string,
  body: {
    name?: string;
    rank?: number;
    permissions?: Record<string, PanelPermission>;
    is_active?: boolean;
  }
): Promise<Role> => {
  const res = await api.put(`/roles/${id}`, body);
  return res.data;
};

export const deleteRole = async (id: string): Promise<void> => {
  await api.delete(`/roles/${id}`);
};

export const getRoleMembers = async (id: string): Promise<RoleMember[]> => {
  const res = await api.get(`/roles/${id}/members`);
  return res.data;
};
